<template>
    <base-panel
        :panel="panel"
        v-on:continue="handleContinue()"
        v-on:edit-action="handleEditAction($event)"
        v-on:update-width="handleUpdateWidth($event)"
        v-on:content-shown="initializeContent()"
        :no-padding="omitPanelPadding"
        ref="basePanel"
    >
        <template slot="content" v-if="panel.display_variation === 'accordion'">
            <div class="accordion" :id="'tabs' + panel.id">
                <div
                    class="card"
                    :class="{ 'border-left-0 border-right-0 card-no-border': omitPanelPadding }"
                    v-for="choice in panel.choices"
                    :key="choice.id"
                >
                    <div class="card-header" :id="'tabs-heading' + choice.id">
                        <h2 class="mb-0">
                            <button
                                class="btn btn-link btn-block text-left"
                                :aria-controls="'tabs-collapse' + choice.id"
                                :aria-expanded="selectedChoice.id === choice.id"
                                :data-target="'#tabs-collapse' + choice.id"
                                data-toggle="collapse"
                                @click="setActiveChoice(choice)"
                            >
                                <span>{{ choice.text }}</span>
                                <div class="align-middle position-relative d-inline-block unexplored-marker-container">
                                    <div
                                        v-show="!choice.isExplored"
                                        aria-label="Tab is unexplored"
                                        class="unexplored-marker rounded-circle"
                                    ></div>
                                </div>
                            </button>
                        </h2>
                    </div>
                    <div
                        :id="'tabs-collapse' + choice.id"
                        :aria-labelledby="'tabs-heading' + choice.id"
                        :data-parent="'#tabs' + panel.id"
                        class="collapse"
                        ref="accordionCollapsible"
                    >
                        <div class="card-body">
                            <rich-content-display
                                :elements="choice.elements"
                                :editable="editable"
                            ></rich-content-display>
                        </div>
                    </div>
                </div>
            </div>
        </template>
        <template slot="content" v-else>
            <ul class="nav nav-tabs">
                <li class="nav-item" v-for="choice in panel.choices" :key="choice.id">
                    <a
                        class="nav-link tab-label"
                        :class="{ active: choice.id == selectedChoice.id }"
                        @click="setActiveChoice(choice)"
                    >
                        <span>{{ choice.text }}</span>
                        <div class="align-middle position-relative d-inline-block unexplored-marker-container">
                            <div
                                v-show="!choice.isExplored"
                                aria-label="Tab is unexplored"
                                class="unexplored-marker rounded-circle"
                            ></div>
                        </div>
                    </a>
                </li>
            </ul>
            <div>
                <div
                    ref="tabContainer"
                    :style="{ 'min-height': minTabContentHeight + 'px' }"
                    class="tab-container bg-white"
                >
                    <rich-content-display
                        :elements="selectedChoice.elements"
                        :editable="editable"
                        v-on:element-updated="resetMinTabHeight()"
                    ></rich-content-display>
                </div>
            </div>
        </template>
    </base-panel>
</template>

<script>
import BasePanel from "@/components/panels/BasePanel"
import RichContentDisplay from "@/components/RichContentDisplay"

export default {
    name: "TabsPanel",
    components: {
        RichContentDisplay,
        BasePanel,
    },
    props: {
        panel: {
            type: Object,
            required: true,
        },
        editable: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            selectedChoice: {
                elements: [],
            },
            minTabContentHeight: 0,
        }
    },
    computed: {
        subheaderIsConfigured() {
            return this.panel.sub_header !== "" && this.panel.sub_header != "<p></p>"
        },
        // Controls whether or not the panel should include the default padding. Omit extra padding
        // if no header or subheader is configured.
        omitPanelPadding() {
            return (
                !this.panel.header &&
                !this.subheaderIsConfigured &&
                this.panel.display_variation == "accordion"
            )
        },
    },
    methods: {
        initializeContent() {
            if (this.panel.display_variation == "default") {
                this.setMinTabHeight()
            } else if (this.panel.display_variation == "accordion") {
                // Expand the first accordion 'tab'.
                this.$refs.accordionCollapsible[0].classList.add("show")
            }
        },
        // Set the tab that's currently being viewed.
        setActiveChoice(choice) {
            this.selectedChoice = choice
            this.$set(this.selectedChoice, "isExplored", true)
        },
        // Set the minimum height of the tab content container. This improves the
        // 'jumping' of the content's height when switching between tabs.
        setMinTabHeight() {
            let vm = this
            this.$nextTick(() => {
                vm.minTabContentHeight = vm.getTabContainerHeight()
            })
        },
        // Reset the minimum height of the tab content container.
        resetMinTabHeight() {
            this.minTabContentHeight = 0
            let vm = this
            this.$nextTick(() => vm.setMinTabHeight())
        },
        // Find the height of the div containing the currently viewed tab elements.
        getTabContainerHeight() {
            return this.$refs.tabContainer.offsetHeight
        },
        handleContinue() {
            this.$emit("continue")
        },
        handleEditAction(action) {
            this.$emit("edit-action", action)
        },
        handleUpdateWidth($event) {
            this.$emit("update-width", $event)
        },
        skipVideos() {
            this.$refs.basePanel.skipVideos()
        },
    },
    created() {
        // Set the first tab as the one that's currently being viewed.
        if (this.panel.choices.length) {
            this.selectedChoice = this.panel.choices[0]
            this.$set(this.selectedChoice, "isExplored", true)
        }
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";

$marker-width: 6px;
$tab-color: $primary;

.card-no-border:first-of-type {
    border-top: 0px;
}

.card-no-border:last-of-type {
    border-bottom: 0px;
}

.card-body {
    padding-top: 16px;
}

@keyframes unex-pulse {
    0% {
        transform: scale(1);
    }
    50% {
        transform: scale(1.2);
    }
    100% {
        transform: scale(1);
    }
}

.unexplored-marker-container {
    width: 2px;
    left: 6px;
    margin-bottom: 1px;
}

.unexplored-marker {
    width: $marker-width;
    height: $marker-width;
    background-color: $primary;
    animation: 1.5s ease-in-out 0s infinite normal both running unex-pulse;
}

.tab-label {
    color: darken($tab-color, 5%);
    &:hover {
        color: darken($tab-color, 15%);
    }
    padding-right: 18px;
    padding-left: 15px;
}

.tab-container {
    padding-top: 20px;
}
</style>